import React, { useState, useEffect, useCallback, useRef } from "react";
import lottie, { AnimationItem } from "lottie-web";
import { Website } from "../types/siteSpecs";
import { trackEvent } from "../analytics";
import { GOOGLE_APPS_SCRIPT_WEB_APP_URL } from "../googleAppsScriptWebApp";
import { recordButtonPress, recordLinkShown, fetchButtonPressCount, displayPressCount, formatPressCount } from "../buttonPressCounter";

const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1IwzYFymsG3qOFELz3iHQE_7b-xul1XzljkaoA1KIrMk/pub?output=csv";
const ADVENTURE_TAB = "dearestAdventure";
const BUTTON_ANIM_PATH = `${process.env.PUBLIC_URL}/animations/button.json`;
const BUTTON_ASSETS_PATH = `${process.env.PUBLIC_URL}/animations/images/`;

/* ── Grid constants ─────────────────────────────────────── */
const GRID_LINE_ALPHA = 0.45;
const GRID_GLOW_RADIUS = 120;
const GRID_GLOW_STRENGTH = 0.2;
const GRID_LINES = 14;
const VOID_INSET = 0.28;
const WAVE_DURATION = 1200;
const WAVE_WIDTH = 0.15;
const WAVE_STRENGTH = 0.5;

function parseCSVRow(line: string): string[] {
  const result: string[] = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') q = !q;
    else if (c === "," && !q) { result.push(cur.trim()); cur = ""; }
    else cur += c;
  }
  result.push(cur.trim());
  return result;
}

function getVisitedSites(): string[] {
  const c = document.cookie.split("; ").find((r) => r.startsWith("visitedSites="));
  if (!c) return [];
  try { const p = JSON.parse(decodeURIComponent(c.split("=")[1])); return Array.isArray(p) ? p : []; }
  catch { return []; }
}

function addVisitedSite(url: string) {
  const v = getVisitedSites();
  if (!v.includes(url)) v.push(url);
  const exp = new Date(); exp.setDate(exp.getDate() + 30);
  document.cookie = `visitedSites=${encodeURIComponent(JSON.stringify(v))}; expires=${exp.toUTCString()}; path=/; SameSite=Lax`;
}

const BURST_PARTICLE_COUNT = 12;
const BURST_PARTICLES = Array.from({ length: BURST_PARTICLE_COUNT }, (_, i) => {
  const angle = (i / BURST_PARTICLE_COUNT) * Math.PI * 2;
  const dist = 50 + Math.random() * 40;
  return {
    dx: `${(Math.cos(angle) * dist).toFixed(1)}px`,
    dy: `${(Math.sin(angle) * dist).toFixed(1)}px`,
    size: `${(1.5 + Math.random() * 2.5).toFixed(1)}px`,
    delay: `${(Math.random() * 0.06).toFixed(3)}s`,
  };
});

interface PipContentProps {
  nsfwEnabled: boolean;
  usefulStuffEnabled: boolean;
  /** Display total from the main page (already includes offset). */
  pressCount: number | null;
}

/* ── Grid canvas background — tunnel/room perspective ──── */
function useGridCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  waveTriggerRef: React.MutableRefObject<number>,
) {
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = parent.offsetWidth;
      canvas.height = parent.offsetHeight;
    };
    resize();

    const onMove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - r.left, y: e.clientY - r.top, active: true };
    };
    const onLeave = () => { mouseRef.current.active = false; };

    const draw = (t: number) => {
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const active = mouseRef.current.active;

      ctx.lineCap = "round";

      const vL = W * VOID_INSET;
      const vR = W * (1 - VOID_INSET);
      const vT = H * VOID_INSET;
      const vB = H * (1 - VOID_INSET);
      const cx = W / 2;
      const cy = H / 2;
      const maxDist = Math.hypot(cx, cy);

      // Click-triggered shockwave
      const elapsed = t - waveTriggerRef.current;
      const waveActive = elapsed >= 0 && elapsed < WAVE_DURATION;
      const wavePhase = waveActive ? elapsed / WAVE_DURATION : -1;

      const drawLine = (
        x1: number, y1: number, x2: number, y2: number,
      ) => {
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;

        // Hover glow
        let g = 0;
        if (active) {
          const d = Math.hypot(midX - mx, midY - my);
          g = Math.max(0, 1 - d / GRID_GLOW_RADIUS);
        }

        // Shockwave glow — ring expanding from center on click
        let w = 0;
        if (waveActive) {
          const dist = Math.hypot(midX - cx, midY - cy) / maxDist;
          const delta = Math.abs(dist - wavePhase);
          w = Math.max(0, 1 - delta / WAVE_WIDTH) * WAVE_STRENGTH * (1 - wavePhase * 0.6);
        }

        const alpha = GRID_LINE_ALPHA + g * GRID_GLOW_STRENGTH + w;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = `rgba(255,255,255,${Math.min(alpha, 1)})`;
        ctx.lineWidth = 0.4 + g * 1.4 + w * 2;
        ctx.stroke();
      };

      // Corner converging lines
      drawLine(0, 0, vL, vT);
      drawLine(W, 0, vR, vT);
      drawLine(0, H, vL, vB);
      drawLine(W, H, vR, vB);

      // Top wall
      for (let i = 1; i <= GRID_LINES; i++) {
        const frac = i / (GRID_LINES + 1);
        const y = vT * frac;
        const xL = vL * frac;
        const xR = W - (W - vR) * frac;
        drawLine(xL, y, xR, y);
        const outerX = W * frac;
        const innerX = vL + (vR - vL) * frac;
        drawLine(outerX, 0, innerX, vT);
      }

      // Bottom wall
      for (let i = 1; i <= GRID_LINES; i++) {
        const frac = i / (GRID_LINES + 1);
        const y = vB + (H - vB) * frac;
        const xL = vL * (1 - frac);
        const xR = W - (W - vR) * (1 - frac);
        drawLine(xL, y, xR, y);
        const outerX = W * frac;
        const innerX = vL + (vR - vL) * frac;
        drawLine(outerX, H, innerX, vB);
      }

      // Left wall
      for (let i = 1; i <= GRID_LINES; i++) {
        const frac = i / (GRID_LINES + 1);
        const x = vL * frac;
        const yT = vT * frac;
        const yB = H - (H - vB) * frac;
        drawLine(x, yT, x, yB);
        const outerY = H * frac;
        const innerY = vT + (vB - vT) * frac;
        drawLine(0, outerY, vL, innerY);
      }

      // Right wall
      for (let i = 1; i <= GRID_LINES; i++) {
        const frac = i / (GRID_LINES + 1);
        const x = vR + (W - vR) * frac;
        const yT = vT * (1 - frac);
        const yB = H - (H - vB) * (1 - frac);
        drawLine(x, yT, x, yB);
        const outerY = H * frac;
        const innerY = vT + (vB - vT) * frac;
        drawLine(W, outerY, vR, innerY);
      }

      // Void rectangle border
      ctx.beginPath();
      ctx.rect(vL, vT, vR - vL, vB - vT);
      ctx.strokeStyle = `rgba(255,255,255,${GRID_LINE_ALPHA * 0.6})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Cursor glow overlay
      if (active) {
        const grd = ctx.createRadialGradient(mx, my, 0, mx, my, GRID_GLOW_RADIUS);
        grd.addColorStop(0, "rgba(255,255,255,0.05)");
        grd.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, W, H);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    canvas.addEventListener("mousemove", onMove);
    canvas.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      canvas.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, [canvasRef, waveTriggerRef]);
}

export default function PipContent({
  nsfwEnabled,
  usefulStuffEnabled,
  pressCount: pressCountFromMain,
}: PipContentProps) {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [lastSite, setLastSite] = useState<Website | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusText, setStatusText] = useState("Loading sites\u2026");
  const [burstKey, setBurstKey] = useState(0);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportThanksVisible, setReportThanksVisible] = useState(false);
  const [pressCount, setPressCount] = useState<number | null>(
    () => pressCountFromMain ?? displayPressCount(0)
  );
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);
  const adventureTabRef = useRef<Window | null>(null);
  const gridCanvasRef = useRef<HTMLCanvasElement>(null);
  const waveTriggerRef = useRef<number>(-Infinity);

  useGridCanvas(gridCanvasRef, waveTriggerRef);

  useEffect(() => {
    if (!reportThanksVisible) return;
    const id = window.setTimeout(() => setReportThanksVisible(false), 2500);
    return () => window.clearTimeout(id);
  }, [reportThanksVisible]);

  // Keep in sync with main page (don't clobber a higher local optimistic count).
  useEffect(() => {
    if (pressCountFromMain === null) return;
    setPressCount((prev) =>
      prev === null ? pressCountFromMain : Math.max(prev, pressCountFromMain)
    );
  }, [pressCountFromMain]);

  useEffect(() => {
    let cancelled = false;
    const refresh = () => {
      void fetchButtonPressCount().then((count) => {
        if (cancelled || count === null) return;
        const next = displayPressCount(count);
        setPressCount((prev) => (prev === null ? next : Math.max(prev, next)));
      });
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useEffect(() => {
    if (!lottieContainerRef.current) return;
    const anim = lottie.loadAnimation({
      container: lottieContainerRef.current,
      renderer: "svg",
      loop: false,
      autoplay: false,
      path: BUTTON_ANIM_PATH,
      assetsPath: BUTTON_ASSETS_PATH,
      rendererSettings: { preserveAspectRatio: "xMidYMid meet", progressiveLoad: true },
    });
    anim.setSpeed(1.08);
    anim.addEventListener("DOMLoaded", () => anim.goToAndStop(0, true));
    animRef.current = anim;
    return () => { anim.destroy(); animRef.current = null; };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(SHEET_URL);
        if (!res.ok) throw new Error("fetch failed");
        const csv = await res.text();
        const rows = csv.split("\n");
        const headers = rows[0].split(",").map((h) => h.replace(/["']/g, "").trim().toLowerCase());
        const col = (name: string) => headers.findIndex((h) => h.includes(name));
        const idxActive = col("active");
        const idxUrl = col("link") !== -1 ? col("link") : col("url");
        const idxNsfw = col("nsfw");
        const idxUseful = col("useful");
        const idxTitle = col("title") !== -1 ? col("title") : col("name");
        const cell = (cols: string[], idx: number) => idx >= 0 ? cols[idx]?.replace(/["']/g, "").trim() : "";
        const list = rows.slice(1).map((line) => {
          const cols = parseCSVRow(line);
          if (!cell(cols, idxActive).toLowerCase().includes("yes")) return null;
          const rawUrl = cell(cols, idxUrl);
          if (!rawUrl) return null;
          const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
          return { url, isNsfw: cell(cols, idxNsfw).toLowerCase().includes("yes"), isUseful: cell(cols, idxUseful).toLowerCase().includes("yes"), title: cell(cols, idxTitle) || "Somewhere fun on the internet" } as Website;
        }).filter((w): w is Website => w !== null);
        if (list.length > 0) setWebsites(list);
        setStatusText("Press the button");
      } catch { setStatusText("Could not load sites"); }
      finally { setLoading(false); }
    })();
  }, []);

  const pickRandom = useCallback((): Website | undefined => {
    if (websites.length === 0) return undefined;
    const eligible = websites.filter((w) => {
      if (w.isNsfw && !nsfwEnabled) return false;
      if (w.isUseful && !usefulStuffEnabled) return false;
      return true;
    });
    const pool = eligible.length > 0 ? eligible : websites;
    const visited = getVisitedSites();
    const unseen = pool.filter((w) => !visited.includes(w.url));
    const finalPool = unseen.length > 0 ? unseen : pool;
    return finalPool[Math.floor(Math.random() * finalPool.length)];
  }, [nsfwEnabled, usefulStuffEnabled, websites]);

  const handleClick = () => {
    if (loading || websites.length === 0) return;
    const site = pickRandom();
    if (!site) return;

    trackEvent("pip_adventure_click");
    recordButtonPress("pip");
    recordLinkShown(site.url);
    setPressCount((prev) =>
      prev === null ? displayPressCount(1) : prev + 1
    );
    const pipWin = lottieContainerRef.current?.ownerDocument?.defaultView;
    const opener = pipWin || window;

    const existing = adventureTabRef.current;
    if (existing && !existing.closed) {
      try {
        existing.location.assign(site.url);
        existing.focus();
      } catch {
        const tab = opener.open(site.url, ADVENTURE_TAB);
        adventureTabRef.current = tab;
        if (tab) try { tab.focus(); } catch { /* cross-origin */ }
      }
    } else {
      const tab = opener.open(site.url, ADVENTURE_TAB);
      adventureTabRef.current = tab;
      if (tab) try { tab.focus(); } catch { /* cross-origin */ }
    }

    if (animRef.current) {
      animRef.current.goToAndStop(0, true);
      animRef.current.play();
    }
    setBurstKey((k) => k + 1);
    waveTriggerRef.current = performance.now();

    setLastSite(site);
    setReportThanksVisible(false);
    setReportError("");
    setStatusText("Bon voyage!");

    Promise.resolve().then(() => {
      addVisitedSite(site.url);
      setTimeout(() => setStatusText("Press the button"), 1500);
    });
  };

  const handleReport = async () => {
    if (!lastSite || reportSubmitting || reportThanksVisible) return;
    trackEvent("pip_report_website");
    setReportError("");
    setReportSubmitting(true);
    try {
      await fetch(GOOGLE_APPS_SCRIPT_WEB_APP_URL, {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify({
          formType: "report",
          url: lastSite.url,
          timestamp: new Date().toISOString(),
        }),
        headers: { "Content-Type": "text/plain" },
      });
      setReportThanksVisible(true);
    } catch {
      setReportError("Couldn't send. Try again.");
    } finally {
      setReportSubmitting(false);
    }
  };

  return (
    <div style={{
      width: "100%",
      height: "100vh",
      background: "#000",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      padding: "14px",
      boxSizing: "border-box",
      overflow: "hidden",
      userSelect: "none",
      position: "relative",
    }}>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 0,
        }}
      >
      {/* Grid background canvas */}
      <canvas
        ref={gridCanvasRef}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "auto" }}
        aria-hidden="true"
      />

      <style>{`
        @keyframes pipTwinkle {
          0%, 100% { opacity: 0.15; }
          50% { opacity: 0.7; }
        }
        .pip-twinkle {
          position: absolute; border-radius: 50%; background: #fff;
          pointer-events: none; animation: pipTwinkle ease-in-out infinite;
        }
        @keyframes pipBurstDot {
          0% { opacity: 0.9; transform: translate(0,0) scale(1); }
          50% { opacity: 0.7; }
          100% { opacity: 0; transform: translate(var(--pip-dx), var(--pip-dy)) scale(0.2); }
        }
        .pip-burst-ring { position:absolute; top:50%; left:50%; width:0; height:0; pointer-events:none; z-index:10; }
        .pip-burst-dot { position:absolute; border-radius:50%; background:#fff; width:var(--pip-dot-size,3px); height:var(--pip-dot-size,3px); opacity:0; animation:pipBurstDot 0.5s cubic-bezier(0.22,1,0.36,1) forwards; animation-delay:var(--pip-burst-delay,0s); }
      `}</style>

      {/* Twinkling stars */}
      <div className="pip-twinkle" style={{ width: 2, height: 2, top: "12%", left: "18%", animationDuration: "3.2s", animationDelay: "0s" }} />
      <div className="pip-twinkle" style={{ width: 1.5, height: 1.5, top: "28%", right: "14%", animationDuration: "4.1s", animationDelay: "1.4s" }} />
      <div className="pip-twinkle" style={{ width: 2.5, height: 2.5, bottom: "22%", left: "25%", animationDuration: "3.7s", animationDelay: "0.8s" }} />
      <div className="pip-twinkle" style={{ width: 1.8, height: 1.8, top: "65%", right: "22%", animationDuration: "5s", animationDelay: "2.2s" }} />
      <div className="pip-twinkle" style={{ width: 1.2, height: 1.2, top: "8%", right: "40%", animationDuration: "3.5s", animationDelay: "0.5s" }} />
      <div className="pip-twinkle" style={{ width: 2, height: 2, bottom: "10%", right: "35%", animationDuration: "4.6s", animationDelay: "1.8s" }} />

      {/* Header */}
      <p
        className="gilroy-bold"
        style={{
          color: "rgba(255,255,255,0.35)",
          fontSize: 11,
          letterSpacing: 2.5,
          textTransform: "uppercase",
          margin: "0 0 14px 0",
          position: "relative",
          zIndex: 1,
        }}
      >
        Dearest Internet
      </p>

      {/* Button */}
      <button
        type="button"
        onClick={handleClick}
        disabled={loading || websites.length === 0}
        aria-label="Launch a random internet adventure"
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: loading ? "default" : "pointer",
          outline: "none",
          width: 160,
          height: 160,
          flexShrink: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          ref={lottieContainerRef}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: "155%",
            height: "155%",
            transform: "translate(-50%, -38%)",
            pointerEvents: "none",
          }}
        />
        {burstKey > 0 && (
          <div key={burstKey} className="pip-burst-ring">
            {BURST_PARTICLES.map((p, i) => (
              <div
                key={i}
                className="pip-burst-dot"
                style={{ "--pip-dx": p.dx, "--pip-dy": p.dy, "--pip-dot-size": p.size, "--pip-burst-delay": p.delay } as React.CSSProperties}
              />
            ))}
          </div>
        )}
        {pressCount !== null && (
          <div
            className="press-count-chip pointer-events-none gilroy-medium"
            style={{
              position: "absolute",
              left: "50%",
              top: 18,
              transform: "translate(-50%, -100%)",
              zIndex: 12,
              whiteSpace: "nowrap",
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(0,0,0,0.55)",
              color: "rgba(255,255,255,0.8)",
              padding: "4px 8px",
              fontSize: 10,
              letterSpacing: "0.01em",
              boxShadow: "0 4px 12px rgba(0,0,0,0.22)",
            }}
          >
            <span className="press-live-dot" aria-hidden="true" />
            <span>
              Pressed {formatPressCount(pressCount)}{" "}
              {pressCount === 1 ? "time" : "times"}
            </span>
          </div>
        )}
      </button>

      {/* Status text */}
      <p
        className="gilroy-regular"
        style={{
          color: "rgba(255,255,255,0.5)",
          fontSize: 11,
          margin: "12px 0 0 0",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {statusText}
      </p>

      {/* Last visited title (no URL) */}
      {lastSite && (
        <div
          style={{
            marginTop: 12,
            padding: "8px 12px",
            background: "rgba(255,255,255,0.06)",
            borderRadius: 10,
            maxWidth: 200,
            width: "100%",
            textAlign: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <p
            className="gilroy-semibold"
            style={{
              color: "#fff",
              fontSize: 11,
              lineHeight: "1.35",
              margin: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical" as const,
              overflow: "hidden",
            }}
          >
            {lastSite.title}
          </p>
        </div>
      )}
      </div>

      {/* Bottom: report (same payload as Last visited card) */}
      <div
        style={{
          flexShrink: 0,
          paddingTop: 8,
          paddingBottom: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 6,
          position: "relative",
          zIndex: 1,
        }}
      >
        {reportThanksVisible ? (
          <p
            className="gilroy-bold"
            style={{
              color: "#fff",
              fontSize: 13,
              lineHeight: 1.35,
              margin: 0,
              textAlign: "center",
            }}
          >
            You are a hero,
            <br />
            cupcakes!
          </p>
        ) : lastSite ? (
          <>
            <button
              type="button"
              onClick={handleReport}
              disabled={reportSubmitting}
              className="gilroy-medium"
              style={{
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 8,
                color: "rgba(255,255,255,0.7)",
                fontSize: 12,
                cursor: reportSubmitting ? "default" : "pointer",
                opacity: reportSubmitting ? 0.5 : 1,
                padding: "6px 14px",
              }}
            >
              {reportSubmitting ? "Sending…" : "⚑ Report website"}
            </button>
            {reportError ? (
              <p className="gilroy-regular" style={{ color: "#f87171", fontSize: 11, margin: 0, textAlign: "center" }}>
                {reportError}
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
